/**
 * Backend-scoped guest-session storage.
 *
 * Guest JWTs are HS256-signed with the API's OMMUSE_SHARE_SECRET, which is a
 * *different* secret per backend stack (prod vs dev). A token minted against
 * one backend therefore fails verification on another, and the request lands in
 * the server's `s2s == false` guards as
 *   PERMISSION_DENIED "user id must match request".
 *
 * That is exactly what happened when app.ommuse.com was mis-routed to the dev
 * API for ~6 weeks: guests registered on dev, cached a dev-signed token, and
 * then — once routing was fixed — started sending it to the prod backend.
 *
 * To make that class of bug structurally impossible, every guest key is
 * namespaced by the API backend's host. A session minted against dev is stored
 * under a dev-scoped key and is simply never read when the app points at prod.
 * `migrateLegacyGuestStorage` clears the old un-namespaced keys written before
 * this change so no stale cross-backend token survives a deploy.
 *
 * This module intentionally has NO proto / grpc-web imports so it stays a pure,
 * unit-testable storage layer. RPC calls live in guestSessionService.ts.
 */

// Base (un-namespaced) key names. Also the exact set of legacy keys to purge.
const GUEST_FLAG_KEY = "ommuse.guest";
const GUEST_TOKEN_KEY = "ommuse.guest.token";
const GUEST_SESSION_ID_KEY = "ommuse.guest.session_id";
const GUEST_EXPIRES_AT_KEY = "ommuse.guest.expires_at_ms";
const GUEST_QUOTA_BYTES_KEY = "ommuse.guest.quota_bytes";
const GUEST_USED_BYTES_KEY = "ommuse.guest.used_bytes";

const LEGACY_GUEST_KEYS = [
  GUEST_FLAG_KEY,
  GUEST_TOKEN_KEY,
  GUEST_SESSION_ID_KEY,
  GUEST_EXPIRES_AT_KEY,
  GUEST_QUOTA_BYTES_KEY,
  GUEST_USED_BYTES_KEY,
];

const DEFAULT_GUEST_QUOTA_BYTES = 100 * 1024 * 1024;

// Fired on window (same-tab) whenever the locally-tracked guest usage changes.
// The native "storage" event only fires in OTHER tabs, so useAuthMode listens
// for this to re-read usage in the tab that performed the upload.
export const GUEST_USAGE_EVENT = "ommuse.guest.usage-changed";

export interface GuestSession {
  token: string;
  sessionId: string;
  expiresAtMs: number;
  quotaBytes: number;
  /**
   * Storage consumed by the session, in bytes. Seeded from the
   * CreateGuestSession response (0 for a new session, the authoritative backend
   * value for a resumed one) and incremented client-side as uploads complete
   * via incrementGuestUsage().
   */
  usedBytes: number;
}

// A stable, human-readable tag for the API backend the app is built against.
// The host is enough to distinguish api.ommuse.com from api-dev.ommuse.com.
export const backendTag = (): string => {
  try {
    return new URL(process.env.REACT_APP_API_URL || "").host || "default";
  } catch {
    return "default";
  }
};

// Namespace a base key to the current backend, e.g.
//   ommuse.guest.token -> ommuse.guest.token::api.ommuse.com
const ns = (base: string): string => `${base}::${backendTag()}`;

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

export const getStoredGuestSession = (): GuestSession | null => {
  const token = safeGet(ns(GUEST_TOKEN_KEY));
  const sessionId = safeGet(ns(GUEST_SESSION_ID_KEY));
  const expiresRaw = safeGet(ns(GUEST_EXPIRES_AT_KEY));
  const quotaRaw = safeGet(ns(GUEST_QUOTA_BYTES_KEY));
  const usedRaw = safeGet(ns(GUEST_USED_BYTES_KEY));
  if (!token || !sessionId || !expiresRaw) return null;
  const expiresAtMs = Number(expiresRaw);
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
  const usedBytes = usedRaw ? Number(usedRaw) : 0;
  return {
    token,
    sessionId,
    expiresAtMs,
    quotaBytes: quotaRaw ? Number(quotaRaw) : DEFAULT_GUEST_QUOTA_BYTES,
    usedBytes: Number.isFinite(usedBytes) && usedBytes > 0 ? usedBytes : 0,
  };
};

export const storeGuestSession = (s: GuestSession) => {
  safeSet(ns(GUEST_TOKEN_KEY), s.token);
  safeSet(ns(GUEST_SESSION_ID_KEY), s.sessionId);
  safeSet(ns(GUEST_EXPIRES_AT_KEY), String(s.expiresAtMs));
  safeSet(ns(GUEST_QUOTA_BYTES_KEY), String(s.quotaBytes));
  safeSet(ns(GUEST_USED_BYTES_KEY), String(Math.max(0, s.usedBytes || 0)));
};

export const clearStoredGuestSession = () => {
  safeRemove(ns(GUEST_FLAG_KEY));
  safeRemove(ns(GUEST_TOKEN_KEY));
  safeRemove(ns(GUEST_SESSION_ID_KEY));
  safeRemove(ns(GUEST_EXPIRES_AT_KEY));
  safeRemove(ns(GUEST_QUOTA_BYTES_KEY));
  safeRemove(ns(GUEST_USED_BYTES_KEY));
};

export const markGuestSession = () => {
  safeSet(ns(GUEST_FLAG_KEY), "1");
};

/**
 * Add deltaBytes to the session's locally-tracked usage and notify the current
 * tab (via GUEST_USAGE_EVENT) so the quota meter/banner re-read immediately.
 * No-op when there is no stored session. Clamps at zero. Returns the new total.
 */
export const incrementGuestUsage = (deltaBytes: number): number => {
  if (safeGet(ns(GUEST_TOKEN_KEY)) === null) return 0;
  const current = safeGet(ns(GUEST_USED_BYTES_KEY));
  const base = current ? Number(current) : 0;
  const next = Math.max(
    0,
    (Number.isFinite(base) ? base : 0) + (deltaBytes || 0)
  );
  safeSet(ns(GUEST_USED_BYTES_KEY), String(next));
  try {
    window.dispatchEvent(new Event(GUEST_USAGE_EVENT));
  } catch {
    /* non-browser env; noop */
  }
  return next;
};

// Read the recorded guest business-expiry for the current backend even after
// the session has lapsed. getStoredGuestSession returns null once expired but
// leaves this breadcrumb behind; the Welcome page uses it to explain why an
// expired guest was bounced back.
export const peekGuestExpiryMs = (): number | null => {
  const raw = safeGet(ns(GUEST_EXPIRES_AT_KEY));
  if (!raw) return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
};

// clearGuestSession is the public name used across the guest components; it is
// identical to clearStoredGuestSession (both drop the whole namespaced session).
export const clearGuestSession = clearStoredGuestSession;

export interface LegacyMigrationResult {
  // True when an un-namespaced token existed AND its recorded business expiry
  // was still in the future — i.e. the visitor had a *live* guest session that
  // predates backend-scoped storage (typically minted on the mis-routed dev
  // backend). Callers use this to decide whether to auto-start a fresh session.
  hadLiveLegacySession: boolean;
}

// Purge the pre-namespacing guest keys. Idempotent: after the first call the
// legacy keys are gone and subsequent calls report hadLiveLegacySession=false.
export const migrateLegacyGuestStorage = (): LegacyMigrationResult => {
  const token = safeGet(GUEST_TOKEN_KEY);
  const expiresRaw = safeGet(GUEST_EXPIRES_AT_KEY);
  const expiresAtMs = Number(expiresRaw);
  const hadLiveLegacySession =
    Boolean(token) &&
    Number.isFinite(expiresAtMs) &&
    expiresAtMs > Date.now();
  LEGACY_GUEST_KEYS.forEach(safeRemove);
  return { hadLiveLegacySession };
};

// grpc-web status codes: 7 = PERMISSION_DENIED, 16 = UNAUTHENTICATED.
const GRPC_PERMISSION_DENIED = 7;
const GRPC_UNAUTHENTICATED = 16;

/**
 * True when an RpcError is the signature of a guest session the *current*
 * backend refuses: the `s2s == false` handler guards throw PERMISSION_DENIED
 * "user id must match request", and a rejected guest JWT surfaces as
 * UNAUTHENTICATED "access token is invalid". Callers can use this to clear a
 * stale guest session and re-register instead of surfacing the raw error.
 */
export const isGuestAuthError = (err: unknown): boolean => {
  const e = err as { code?: number; message?: string } | null | undefined;
  if (!e || typeof e.code !== "number") return false;
  const msg = (e.message || "").toLowerCase();
  if (e.code === GRPC_PERMISSION_DENIED && msg.includes("user id must match request")) {
    return true;
  }
  if (e.code === GRPC_UNAUTHENTICATED && msg.includes("access token is invalid")) {
    return true;
  }
  return false;
};
